/**
 * The Growth commitment offer — 6 months prepaid at 50% off — offered to orgs
 * that have never paid, as the replacement for the sunset Starter plan.
 *
 * TWO WAYS TO BUY GROWTH, and the discount is the reward for committing:
 *   - month-to-month at the full $99/mo, cancel any time; or
 *   - 6 months paid UP FRONT for $297 (6 × $49.50), then month-to-month at $99.
 * The discount is deliberately NOT available month-to-month — the prepayment is
 * the point, so a discounted subscriber cannot cancel in month two.
 *
 * MECHANISM (validated against Stripe, not assumed): a single subscription on
 * the ORDINARY monthly Growth price, created with `trial_end` 6 months out and
 * TWO Checkout `line_items` — monthly Growth plus the one-time $297 commitment
 * price. Stripe bills the $297 immediately (`billing_reason: subscription_create`)
 * and then starts charging $99/mo automatically when the trial ends — so the
 * month-7 rollover needs no cron, no subscription schedule, and no second
 * lifecycle path. Checkout also sets `payment_method_collection: 'always'` so
 * month 7 has a card on file.
 *
 * Crucially the subscription ITEM stays the monthly Growth price, so
 * `getPlanKeyFromPriceId` resolves `growth` throughout and the entire webhook /
 * entitlement / reconciler path is untouched. The only backend change the term
 * requires is that a period covering N months grants N months of allowance
 * (`getPeriodMonthMultiplier`).
 *
 * This module is PURE and isomorphic — it is imported by client components
 * (pricing cards, the onboarding step), so it must never read `process.env`.
 * The commitment price id is resolved server-side from the Convex deployment
 * env named by `commitPriceEnvVar`.
 */

import type { BillingPlanKey, SelfServePlanKey } from './plans'
import { formatCurrency } from './price-summary'

/** Typed separately so the promo target is compile-checked as a real tier. */
const PROMO_PLAN_KEY: SelfServePlanKey = 'growth'

export const GROWTH_PROMO = {
  /**
   * The kill switch. One boolean, one file — flip to `false` to withdraw the
   * commitment offer from every surface at once (pricing cards, upgrade
   * dialogs, the onboarding step, and the checkout path). Month-to-month at
   * full price keeps working untouched.
   */
  enabled: true,
  planKey: PROMO_PLAN_KEY,
  percentOff: 50,
  /** Months prepaid in one charge. Also the Stripe trial length. */
  termMonths: 6,
  /**
   * Convex deployment env var holding the one-time Stripe price for the
   * commitment charge. Read ONLY server-side.
   */
  commitPriceEnvVar: 'BILLING_STRIPE_GROWTH_COMMIT_PRICE_ID',
  /** Stamped onto the subscription metadata for reporting. */
  metadataKey: 'promo',
  metadataValue: 'growth_commit_6mo',
} as const

/**
 * Whether the redesigned onboarding keeps its dedicated promo step (shown
 * after "Invite your team" for eligible orgs). One line, one file: flip to
 * `false` to drop the step from onboarding WITHOUT withdrawing the Growth
 * commitment offer everywhere else — `GROWTH_PROMO.enabled` remains the
 * global kill switch and still gates this step too.
 */
export const SHOW_ONBOARDING_PROMO_STEP = true

/** How a buyer chose to pay. Threaded from the CTA to Stripe. */
export type BillingCadence = 'monthly' | 'commit'

/** Exact Stripe subscription marker for the prepaid Growth commitment. */
export function hasGrowthCommitmentMetadata(
  metadata: Record<string, string> | null | undefined
): boolean {
  return metadata?.[GROWTH_PROMO.metadataKey] === GROWTH_PROMO.metadataValue
}

/**
 * Who may take the commitment offer: orgs currently on `free` that have NEVER
 * had paid billing. A churned customer is deliberately excluded — `hasEverPaid`
 * stays true for them because the monotonic paid-history flag survives
 * `applySubscriptionDeleted` (legacy rows fall back to paid period records).
 *
 * Month-to-month Growth is NOT gated by this — anyone can buy it at full price.
 */
export function isPromoEligible(args: {
  planKey: BillingPlanKey
  hasEverPaid: boolean
  targetPlanKey?: BillingPlanKey
}): boolean {
  if (!GROWTH_PROMO.enabled) {
    return false
  }
  if (
    args.targetPlanKey !== undefined &&
    args.targetPlanKey !== GROWTH_PROMO.planKey
  ) {
    return false
  }
  return args.planKey === 'free' && !args.hasEverPaid
}

/**
 * The onboarding wizard's promo-step gate, extracted pure so it is testable
 * without the server component. `app/(onboarding)/setup/page.tsx` resolves the
 * two reads and calls this; the READ itself must stay UN-gated on the wizard
 * step (the server component renders exactly once, before any step cookie
 * exists — see the comment there).
 *
 * `brandLimit.used <= 1` (not `=== 1`): a zero-brand org is the normal
 * first-render state, and `> 1` means this is not the org's first brand.
 */
export function shouldShowOnboardingPromoStep(args: {
  isAddBrand: boolean
  brandLimit: { used: number } | null | undefined
  billingOverview:
    | { planKey: BillingPlanKey; hasEverPaid: boolean }
    | null
    | undefined
}): boolean {
  if (!SHOW_ONBOARDING_PROMO_STEP) {
    return false
  }
  if (args.isAddBrand || !GROWTH_PROMO.enabled) {
    return false
  }
  if (!args.brandLimit || args.brandLimit.used > 1) {
    return false
  }
  if (!args.billingOverview) {
    return false
  }
  return isPromoEligible({
    planKey: args.billingOverview.planKey,
    hasEverPaid: args.billingOverview.hasEverPaid,
  })
}

/** Discounted per-month rate during the committed term. */
export function applyPromoDiscountCents(monthlyCents: number): number {
  return Math.round((monthlyCents * (100 - GROWTH_PROMO.percentOff)) / 100)
}

/** The single up-front charge: the discounted rate × the whole term. */
export function getCommitTotalCents(monthlyCents: number): number {
  return applyPromoDiscountCents(monthlyCents) * GROWTH_PROMO.termMonths
}

/**
 * Unix seconds when the prepaid term ends: `termMonths` CALENDAR months from
 * `now`. Calendar months, not 30-day months — "6 months upfront" must end on
 * the same UTC day of month, clamped to the target month's final day. For
 * example, August 31 ends February 28 (or 29 in a leap year), never March.
 * Doing the calculation entirely in UTC also keeps the term independent of
 * local daylight-saving transitions.
 */
export function getCommitTrialEndSeconds(now: Date): number {
  const end = new Date(now.getTime())
  const originalDay = end.getUTCDate()

  // Move from a safe day first so setUTCMonth cannot roll into the following
  // month before we have a chance to clamp the original day.
  end.setUTCDate(1)
  end.setUTCMonth(end.getUTCMonth() + GROWTH_PROMO.termMonths)
  const finalDayOfTargetMonth = new Date(
    Date.UTC(end.getUTCFullYear(), end.getUTCMonth() + 1, 0)
  ).getUTCDate()
  end.setUTCDate(Math.min(originalDay, finalDayOfTargetMonth))

  return Math.floor(end.getTime() / 1000)
}

/**
 * What Stripe will actually charge for a purchase at this cadence — the whole
 * prepaid term for `commit`, one month at list for `monthly`. Every analytics
 * call site reports THIS (so `checkout_started` matches the session's
 * `amount_total`) instead of re-deriving the split locally.
 */
export function getChargedPriceCents(
  cadence: BillingCadence,
  price: { amountCents: number }
): number {
  return cadence === 'commit'
    ? getCommitTotalCents(price.amountCents)
    : price.amountCents
}

export type PromoPriceLabels = {
  /** Effective monthly rate across the term, e.g. `$49.50`. */
  discountedLabel: string
  /** Undiscounted monthly list price, e.g. `$99`. */
  listLabel: string
  /** The actual amount charged today, e.g. `$297`. */
  commitTotalLabel: string
  /** Card ribbon, e.g. `50% off`. */
  ribbonLabel: string
  /** What the charge buys, e.g. `$297 billed once for 6 months`. */
  billedOnceLabel: string
  /** What happens next, e.g. `Then $99/month — cancel any time`. */
  thenLabel: string
  /** Complete payment, refund, cancellation, access, and renewal disclosure. */
  checkoutDisclosure: string
  /** Total saved versus paying monthly, e.g. `Save $297`. */
  savingsLabel: string
  /** Featured buy CTA, e.g. `Pay $297 for 6 months`. */
  commitCtaLabel: string
  /** Month-to-month alternative, e.g. `or $99/month`. */
  monthlyCtaLabel: string
  /** Price-area caption, e.g. `$297 billed once for 6 months`. */
  priceCaption: string
  discountedAmountCents: number
  commitTotalCents: number
}

/**
 * Build every promo string from one monthly price. `formatCurrency` renders
 * `$99` and `$49.50` correctly (it drops the fraction only for whole amounts),
 * so there is no separate formatter here.
 */
export function buildPromoPriceLabels(price: {
  amountCents: number
  currency: string
}): PromoPriceLabels {
  const discountedAmountCents = applyPromoDiscountCents(price.amountCents)
  const commitTotalCents = getCommitTotalCents(price.amountCents)
  const money = (cents: number) =>
    formatCurrency({ amount: cents / 100, currency: price.currency })

  const listLabel = money(price.amountCents)
  const commitTotalLabel = money(commitTotalCents)
  const savings = price.amountCents * GROWTH_PROMO.termMonths - commitTotalCents
  const savingsLabel = `Save ${money(savings)}`
  const billedOnceLabel = `${commitTotalLabel} billed once for ${GROWTH_PROMO.termMonths} months`

  return {
    discountedLabel: money(discountedAmountCents),
    listLabel,
    commitTotalLabel,
    ribbonLabel: `${GROWTH_PROMO.percentOff}% off`,
    billedOnceLabel,
    thenLabel: `Then ${listLabel}/month, cancel any time`,
    checkoutDisclosure: `${commitTotalLabel} charged today, non-refundable; cancellation preserves access through the term and only prevents the ${listLabel}/month renewal.`,
    savingsLabel,
    commitCtaLabel: `Pay ${commitTotalLabel} for ${GROWTH_PROMO.termMonths} months`,
    monthlyCtaLabel: `or ${listLabel}/month`,
    priceCaption: billedOnceLabel,
    discountedAmountCents,
    commitTotalCents,
  }
}
